import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import {
  CheckCircle, XCircle, CircleNotch, MinusCircle, Clock,
  CaretDown, ArrowsClockwise, ShieldCheck, ShieldWarning,
  Check, X,
} from '@phosphor-icons/react';

const STEP_ICON = {
  completed: CheckCircle,
  running: CircleNotch,
  failed: XCircle,
  skipped: MinusCircle,
  waiting: Clock,
  approval_waiting: ShieldWarning,
};

const STEP_COLOR = {
  completed: 'text-[#00E5C0]',
  running: 'text-[#3B82F6]',
  failed: 'text-[#EF4444]',
  skipped: 'text-[#5C6778]',
  waiting: 'text-[#3A4150]',
  approval_waiting: 'text-[#F59E0B]',
};

const STEP_BG = {
  completed: 'bg-[rgba(0,229,192,0.08)]',
  running: 'bg-[rgba(59,130,246,0.08)]',
  failed: 'bg-[rgba(239,68,68,0.08)]',
  skipped: 'bg-[rgba(107,118,133,0.05)]',
  waiting: 'bg-[rgba(58,65,80,0.05)]',
  approval_waiting: 'bg-[rgba(245,158,11,0.08)]',
};

export default function RunStepBreakdown({ runId, onRetry, onApprovalAction }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedError, setExpandedError] = useState(null);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const fetchSteps = useCallback(async () => {
    if (!runId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/system-runs/${runId}/steps`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch {}
    finally { setLoading(false); }
  }, [runId]);

  useEffect(() => { fetchSteps(); }, [fetchSteps]);

  // Auto-refresh if running or waiting for approval
  useEffect(() => {
    if (!data || (data.run_status !== 'running' && data.run_status !== 'waiting')) return;
    const interval = setInterval(fetchSteps, 3000);
    return () => clearInterval(interval);
  }, [data, fetchSteps]);

  const handleApprove = async () => {
    setApproving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/system-runs/${runId}/approve`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        fetchSteps();
        if (onApprovalAction) onApprovalAction('approved');
      }
    } catch {}
    finally { setApproving(false); }
  };

  const handleReject = async () => {
    setRejecting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/system-runs/${runId}/reject`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        fetchSteps();
        if (onApprovalAction) onApprovalAction('rejected');
      }
    } catch {}
    finally { setRejecting(false); }
  };

  if (loading) return null;
  if (!data || !data.steps || data.steps.length === 0) return null;

  const { steps, metadata, completed_steps, total_steps, failed_step_index, run_status, waiting_for_approval } = data;

  return (
    <div data-testid="run-step-breakdown">
      {/* Progress summary */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] text-[#5C6778]">
          {run_status === 'completed' && `${completed_steps}/${total_steps} steps completed`}
          {run_status === 'running' && `${completed_steps}/${total_steps} steps done`}
          {run_status === 'failed' && `Failed at step ${(failed_step_index ?? 0) + 1} of ${total_steps}`}
          {run_status === 'waiting' && waiting_for_approval && `Waiting for your approval (${completed_steps}/${total_steps} done)`}
          {run_status === 'waiting' && !waiting_for_approval && `Paused (${completed_steps}/${total_steps} done)`}
        </span>
        {run_status === 'running' && (
          <CircleNotch size={11} className="text-[#3B82F6] animate-spin" />
        )}
      </div>

      {/* Step list */}
      <div className="space-y-0" data-testid="step-list">
        {steps.map((step, i) => {
          const Icon = STEP_ICON[step.status] || Clock;
          const color = STEP_COLOR[step.status] || 'text-[#3A4150]';
          const bg = STEP_BG[step.status] || 'bg-[rgba(58,65,80,0.05)]';
          const isRunning = step.status === 'running';
          const isFailed = step.status === 'failed';
          const isApprovalWaiting = step.status === 'approval_waiting';
          const showDetail = expandedError === step.index;

          return (
            <div key={i}>
              <div
                className={`flex items-center gap-3 py-2.5 ${isFailed ? 'cursor-pointer' : ''}`}
                onClick={isFailed && step.error_detail ? () => setExpandedError(showDetail ? null : step.index) : undefined}
                data-testid={`step-${i}-${step.status}`}
              >
                <div className={`w-5 h-5 rounded-full ${bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon
                    size={11}
                    className={`${color} ${isRunning ? 'animate-spin' : ''} ${isApprovalWaiting ? 'animate-pulse' : ''}`}
                    weight={step.status === 'completed' ? 'fill' : 'bold'}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <span className={`text-[13px] ${step.status === 'waiting' || step.status === 'skipped' ? 'text-[#5C6778]' : isApprovalWaiting ? 'text-[#F59E0B]' : 'text-[#C8D1DB]'}`}>
                    {step.label}
                  </span>
                  {isApprovalWaiting && (
                    <span className="block text-[10px] text-[#F59E0B]/70 mt-0.5">Waiting for your approval</span>
                  )}
                  {step.approved_by && (
                    <span className="block text-[10px] text-[#00E5C0]/60 mt-0.5">Approved</span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {step.duration_ms && step.status === 'completed' && (
                    <span className="text-[9px] text-[#2A2F38]">
                      {step.duration_ms < 1000 ? `${step.duration_ms}ms` : `${(step.duration_ms / 1000).toFixed(1)}s`}
                    </span>
                  )}
                  {step.approval_required && step.status === 'waiting' && (
                    <span className="text-[9px] text-[#F59E0B]/50 bg-[rgba(245,158,11,0.06)] px-1.5 py-0.5 rounded">Needs approval</span>
                  )}
                  {isFailed && step.error_detail && (
                    <CaretDown size={10} className={`text-[#5C6778] transition-transform ${showDetail ? 'rotate-180' : ''}`} />
                  )}
                </div>
              </div>

              {/* Approval card */}
              {isApprovalWaiting && (
                <div className="ml-8 mb-2 mt-1" data-testid="approval-card">
                  <div className="rounded-xl bg-[rgba(245,158,11,0.04)] border border-[rgba(245,158,11,0.12)] p-4">
                    <p className="text-[12px] text-[#C8D1DB] font-medium mb-1" data-testid="approval-preview">
                      {step.approval_preview || `Ready to ${step.label.toLowerCase()}`}
                    </p>
                    <p className="text-[10px] text-[#5C6778] mb-3">
                      This step needs your approval before it runs.
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleApprove}
                        disabled={approving || rejecting}
                        data-testid="approve-btn"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[rgba(0,229,192,0.1)] hover:bg-[rgba(0,229,192,0.15)] border border-[rgba(0,229,192,0.2)] text-[11px] text-[#00E5C0] font-medium transition-colors disabled:opacity-50"
                      >
                        {approving ? <CircleNotch size={11} className="animate-spin" /> : <Check size={11} weight="bold" />}
                        {approving ? 'Approving...' : 'Approve'}
                      </button>
                      <button
                        onClick={handleReject}
                        disabled={approving || rejecting}
                        data-testid="reject-btn"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[rgba(239,68,68,0.04)] hover:bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.08)] text-[11px] text-[#7D8896] hover:text-[#EF4444] font-medium transition-colors disabled:opacity-50"
                      >
                        {rejecting ? <CircleNotch size={11} className="animate-spin" /> : <X size={11} weight="bold" />}
                        {rejecting ? 'Rejecting...' : 'Reject'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Error display */}
              {isFailed && step.error && (
                <div className="ml-8 mb-1">
                  <span className="text-[11px] text-[#EF4444]">{step.error}</span>
                  {showDetail && step.error_detail && (
                    <div className="mt-1.5 px-3 py-2 rounded-lg bg-[rgba(239,68,68,0.04)] border border-[rgba(239,68,68,0.08)]" data-testid={`step-${i}-detail`}>
                      <pre className="text-[10px] text-[#B0B8C4] font-mono whitespace-pre-wrap break-all">{step.error_detail}</pre>
                    </div>
                  )}
                </div>
              )}

              {/* Connector line */}
              {i < steps.length - 1 && (
                <div className="flex ml-2.5">
                  <div className={`w-px h-2 ${step.status === 'completed' ? 'bg-[rgba(0,229,192,0.15)]' : isApprovalWaiting ? 'bg-[rgba(245,158,11,0.15)]' : 'bg-[rgba(255,255,255,0.04)]'}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Execution metadata */}
      {metadata && Object.keys(metadata).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-[rgba(255,255,255,0.08)]" data-testid="run-metadata">
          {metadata.optimised && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-[rgba(0,229,192,0.04)] border border-[rgba(0,229,192,0.08)]">
              <ShieldCheck size={10} className="text-[#00E5C0]" />
              <span className="text-[10px] text-[#00E5C0]">{metadata.optimised}</span>
            </div>
          )}
          {metadata.providers && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
              <span className="text-[10px] text-[#7D8896]">{metadata.providers}</span>
            </div>
          )}
          {metadata.fallback && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-[rgba(245,158,11,0.04)] border border-[rgba(245,158,11,0.08)]">
              <ArrowsClockwise size={10} className="text-[#F59E0B]" />
              <span className="text-[10px] text-[#F59E0B]">{metadata.fallback}</span>
            </div>
          )}
        </div>
      )}

      {/* Retry from failure */}
      {failed_step_index !== null && failed_step_index !== undefined && onRetry && (
        <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.08)]" data-testid="step-retry-section">
          <button
            onClick={() => onRetry(runId)}
            data-testid="retry-from-failure-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[rgba(239,68,68,0.06)] hover:bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.12)] text-[11px] text-[#EF4444] font-medium transition-colors"
          >
            <ArrowsClockwise size={11} /> Retry this run
          </button>
        </div>
      )}
    </div>
  );
}
